/**
 * FriendFace — the ONE place the friends system decides how to draw a person's
 * face. When the admin "Avatar System" switch is ON (config.avatar_system_enabled)
 * and the person has an avatar_config, it renders their full custom avatar cropped
 * into the same circle/rounded tile. Otherwise it falls back to the current sticker
 * emoji (or a name initial) — so stickers stay the default until avatars are
 * turned on. Drop-in for the old `<span className=... style=...>{glyph(c)}</span>`.
 */
import React from "react";
import { getStickerEmoji } from "./StickerPicker";
import { CHARACTERS, BACKGROUNDS } from "./AvatarBuilder";
import { useFriendConfig } from "../hooks/useFriendConfig";
import { getFrameStyle, getFrameBackdropStyle, FrameScene, ensureFrameKeyframes, animationsOn } from "./avatarFrames";

const initialOf = (c) => (c?.display_name || c?.username || "?")[0]?.toUpperCase() || "?";

export default function FriendFace({ friend, className = "", style = {}, testId }) {
  const { config } = useFriendConfig();
  ensureFrameKeyframes();
  const c = friend || {};
  const cfg = c.avatar_config;
  const frame = c.avatar_frame;
  const frameStyle = getFrameStyle(frame);
  const hasFrame = frame && frame !== "none";

  // Highest priority: an AI-generated / uploaded profile photo the person equipped.
  // This is a full portrait image, cover-fit into the same circle/tile so friends
  // see the real avatar of the person instead of a sticker. A see-through cut-out
  // floats over the frame's animated scene + backdrop glow.
  const photoUrl = c.avatar_image_url;
  if (photoUrl) {
    return (
      <span
        className={className}
        data-testid={testId}
        style={{ ...style, ...frameStyle, ...(hasFrame ? getFrameBackdropStyle(frame) : {}), position: "relative", overflow: "hidden", padding: 0 }}
      >
        <FrameScene frameId={frame} />
        <img
          src={photoUrl}
          alt={c.display_name || c.username || "avatar"}
          draggable={false}
          style={{ position: "relative", width: "100%", height: "100%", objectFit: "cover", display: "block", animation: animationsOn() ? "iwBob 3.6s ease-in-out infinite" : "none" }}
        />
      </span>
    );
  }

  const useAvatar = !!(config?.avatar_system_enabled && cfg && (cfg.character_id || cfg.custom_image));

  if (useAvatar) {
    const char = CHARACTERS.find((ch) => ch.id === cfg.character_id) || CHARACTERS[0];
    const bg = BACKGROUNDS.find((b) => b.id === cfg.background) || BACKGROUNDS[0];
    const img = cfg.custom_image || char.image;
    return (
      <span
        className={className}
        data-testid={testId}
        style={{
          ...style,
          ...frameStyle,
          background: `linear-gradient(135deg, ${bg.colors[0]}, ${bg.colors[1]})`,
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "center",
          overflow: "hidden",
          position: "relative",
        }}
      >
        <FrameScene frameId={frame} />
        <img
          src={img}
          alt={char.name}
          draggable={false}
          style={{
            position: "relative",
            width: "118%",
            height: "118%",
            objectFit: "contain",
            objectPosition: "bottom center",
            filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.35))",
          }}
        />
      </span>
    );
  }

  return (
    <span className={className} data-testid={testId} style={{ ...style, ...frameStyle, ...(hasFrame ? getFrameBackdropStyle(frame) : {}), position: "relative", overflow: "hidden" }}>
      <FrameScene frameId={frame} />
      <span style={{ position: "relative" }}>{getStickerEmoji(c.sticker) || initialOf(c)}</span>
    </span>
  );
}
